import { useEffect, useState, useCallback } from 'react';
import {
  makeStyles,
  Popover,
  Button,
  InputBase,
  InputAdornment,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  Typography,
  Radio,
} from '@material-ui/core';
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown';
import SearchIcon from '@material-ui/icons/Search';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import useFetchList from '../../../../../hooks/useFetchList';

const useStyles = makeStyles((theme) => ({
  // Основной контейнер селектора (как в SortSelector)
  container: {
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexDirection: 'row',
    backgroundColor: 'rgba(208, 208, 208, 0.13)',
    borderRadius: 10,
    transition: 'background-color .3s linear',
    maxWidth: '100%',
    // minWidth: 'max-content',
    '&:hover': {
      transition: 'background-color .3s linear',
      backgroundColor: 'rgba(208,208,208,0.24)',
    },
  },
  containerFlex: {
    display: 'flex',
    justifyContent: 'flex-start',
    alignItems: 'baseline',
    flexDirection: 'row',
    padding: '0px 6px 0px 14px',
    width: 'calc(100% - 32px)',
  },
  titleGray: {
    fontWeight: 400,
    color: 'gray',
    fontSize: '12px',
    marginRight: '8px',
  },
  titleBlack: {
    color: 'black',
    fontSize: '14px',
    marginRight: '2px',

    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  chevron: {
    borderLeft: '1px solid rgb(218, 218, 218)',
    display: 'flex',
    height: '100%',
    padding: '8px',
  },
  chevronIcon: {
    width: 16,
    height: 16,
    fill: 'gray',
    color: 'gray',
    transform: 'rotate( 0deg )',
    transition: 'transform .3s linear',
  },

  // Стили попапа (как в SelectWithDynamicHintFilter)
  modal: {
    display: 'flex',
    flexDirection: 'column',
    width: 400,
    maxHeight: 500,
    overflow: 'hidden',
  },
  paper: {
    backgroundColor: theme.palette.background.paper,
  },

  // Поиск
  searchContainer: {
    padding: '12px',
    borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
  },
  searchInput: {
    fontSize: 14,
    padding: '8px 12px',
    border: '1px solid #e1e4e8',
    borderRadius: 8,
    width: '100%',
  },

  // Список аккаунтов
  listContainer: {
    flex: 1,
    overflow: 'auto',
    maxHeight: 300,
    minHeight: 200,
  },
  listItem: {
    padding: '6px 24px',
    borderBottom: '1px solid rgb(244, 244, 244)',
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: 'rgba(0, 0, 0, 0.04)',
    },
  },
  listItemSelected: {
    backgroundColor: 'rgba(158, 190, 222, 0.12)',
    '&:hover': {
      backgroundColor: 'rgba(25, 118, 210, 0.16)',
    },
  },

  // Кнопки
  buttonsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px',
    borderTop: '1px solid rgba(0, 0, 0, 0.12)',
  },
  button: {
    minWidth: 80,
  },

  // Загрузка
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: theme.spacing(2),
  },

  // Пустое состояние
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(3),
    color: '#666',
  },

  // Красная кнопка Clear
  clearButton: {
    backgroundColor: '#dc3545',
    borderColor: '#dc3545',
    color: 'white',
    '&:hover': {
      backgroundColor: '#c82333',
      borderColor: '#bd2130',
    },
  },
}));

const AccountSelector = ({
                           selectedAccount = null,
                           onAccountChange,
                           includeCurrentAccount = false,
                         }) => {
  const classes = useStyles();
  const { en: { btn, tables } } = useSelector((state) => state.i18n);
  const { accountUuid: paramAccountUuid } = useParams();
  const { metadata: { currentAccount } } = useSelector((state) => state.user);

  // Состояние попапа
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // Состояние поиска
  const [searchQuery, setSearchQuery] = useState('');

  // Локальное состояние для попапа
  const [tempSelectedAccount, setTempSelectedAccount] = useState(selectedAccount);

  // useFetchList для получения аккаунтов
  const defaultEndpoint = `/accounts/${paramAccountUuid || currentAccount.uuid}/account-lookups`;
  const {
    items: accounts,
    totalCount,
    loading,
    onLoadMoreRows,
    filter,
    onFilter,
    clearFilter,
  } = useFetchList({
    baseEndpoint: defaultEndpoint,
    baseParams: {
      fields: ['businessName', 'customIdentifier', 'uuid'],
      sort: 'businessName',
      includeCurrentAccount: includeCurrentAccount,
    },
    canLoad: open,
  });

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
    setTempSelectedAccount(selectedAccount);
  };

  const handleClose = () => {
    setAnchorEl(null);
    setTempSelectedAccount(selectedAccount);
    setSearchQuery('');
  };


  // Поиск с использованием useFetchList
  useEffect(() => {
    if (open) {
      const searchFilter = searchQuery
        ? [{ id: 'businessName', value: searchQuery }]
        : [];
      onFilter(() => searchFilter, true); // true для debounce
    } else if (filter && filter.length) {
      clearFilter(); // true для debounce
    }
  }, [searchQuery, open, onFilter]);

  // Infinity scroll с useFetchList
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop <= clientHeight + 100 && !loading && accounts && accounts.length < totalCount) {
      onLoadMoreRows(accounts.length);
    }
  }, [loading, accounts, totalCount, onLoadMoreRows]);

  return (
    <>
      <div className={classes.container} onClick={handleClick}>
        <div className={classes.containerFlex}>
          <div className={classes.titleGray}>Account:</div>
          <div className={classes.titleBlack}>
            {selectedAccount?.businessName || 'Select Account'}
          </div>
        </div>
        <div className={classes.chevron}>
          <KeyboardArrowDownIcon className={classes.chevronIcon} />
        </div>
      </div>

      <Popover
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        classes={{ paper: classes.paper }}
      >
        <div className={classes.modal}>
          {/* Поиск */}
          <div className={classes.searchContainer}>
            <InputBase
              placeholder="Search accounts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={classes.searchInput}
              startAdornment={
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              }
            />
          </div>

          {/* Список аккаунтов */}
          <div className={classes.listContainer} onScroll={handleScroll}>
            {accounts && accounts.length > 0 ? (
              <List>
                {accounts.map(({ name: businessName, id: uuid }) => (
                  <ListItem
                    key={uuid}
                    className={`${classes.listItem} ${
                      tempSelectedAccount?.uuid === uuid ? classes.listItemSelected : ''
                    }`}
                    onClick={() => {
                      setTempSelectedAccount({
                        businessName,
                        uuid,
                      });
                      if (onAccountChange) {
                        onAccountChange({
                          businessName,
                          uuid,
                        });
                      }
                      setAnchorEl(null);
                      setSearchQuery('');
                    }}
                  >
                    <Radio
                      checked={tempSelectedAccount?.uuid === uuid}
                      size="small"
                      color="primary"
                      style={{
                        marginLeft: -12,
                      }}
                    />
                    <ListItemText primary={businessName} />
                  </ListItem>
                ))}
              </List>
            ) : loading ? (
              <div className={classes.loadingContainer}>
                <CircularProgress size={24} />
                <Typography variant="body2" style={{ marginLeft: 8 }}>
                  Loading accounts...
                </Typography>
              </div>
            ) : (
              <div className={classes.emptyState}>
                <Typography variant="body2">
                  {searchQuery ? 'No accounts found' : 'No accounts available'}
                </Typography>
              </div>
            )}

            {/* Загрузка следующей страницы */}
            {loading && accounts && accounts.length > 0 && (
              <div className={classes.loadingContainer}>
                <CircularProgress size={20} />
              </div>
            )}
          </div>

          {/* Кнопки */}
          <div className={classes.buttonsContainer}>
            <Button
              onClick={handleClose}
              className={classes.button}
            >
              {btn.cancel || 'Cancel'}
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                setTempSelectedAccount(null);
                if (onAccountChange) {
                  onAccountChange(null);
                }
                setAnchorEl(null);
                setSearchQuery('');
              }}
              className={`${classes.button} ${classes.clearButton}`}
            >
              Clear
            </Button>
          </div>
        </div>
      </Popover>
    </>
  );
};

export default AccountSelector;
